// @flow
/* -----------------Globals--------------- */
import React from 'react';

/* -----------------Third parties--------------- */
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import Image from 'next/image';

/* -----------------UI--------------- */
import {
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tooltip,
  Typography,
  Box,
  useMediaQuery,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';

/* -----------------Icons--------------- */
import { CalendarClock } from 'mdi-material-ui';
import { StudyPackOutline } from '@components/icons';

/* -----------------Constants--------------- */
import {
  STUDY_CONTENT_ICON,
  DESCRIPTION_MIN_WIDTH,
  RADIUS_LARGE,
  INNER_RADIUS_LARGE,
  LEARNING_CONTENT_HERO_ICON,
} from './constants';

/* -----------------Types--------------- */
import type {
  Owner,
  TermMetadata,
  ContentTypes,
  FolderItemsCount,
} from 'common/types';
import { folderType } from 'common/types';
import type {
  BundleDetails,
} from 'common/store/types';

/* -----------------Utils--------------- */
import { isEmpty } from 'common/simpleUtils';
import {
  getContentItemText,
  getSmartFolderDesc,
} from 'common/content/appcontentutils';
import {
  getContentIcon,
  getSmartFolderIcon,
} from '../../common/utils';
import {
  trackCustomEvent,
} from '../../analytics/tracking';
import {
  events,
} from 'common/analytics/constants';

/* -----------------Assets--------------- */
import DefaultAvatar from '@images/splash/AvatarSample.png';

/* -----------------Child components--------------- */
import Link from './link';
import ProgressRingUnAuth from './ProgressRingUnAuth';
import RenderMarkdown from '@components/common/RenderMarkdown';

const ProgressRing = dynamic(() => import('./ProgressRing'), {
  ssr: false,
  loading: () => (
    <Skeleton
      variant="circle"
      width={88}
      height={88}
    />
  ),
});

/* -----------------Styles--------------- */
const styles = theme => ({
  listItem: {
    padding: 0,
    minHeight: 88,
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
    },
  },
  listAvatar: {
    backgroundColor: 'inherit',
    color: 'inherit',
    [theme.breakpoints.up('md')]: {
      marginRight: theme.spacing(2),
    },
  },
  overlineOwnerLink: {
    color: theme.palette.secondary.main,
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    textTransform: 'none',
    letterSpacing: 0.5,
    '&:hover': {
      color: theme.palette.secondary.dark,
      filter: 'brightness(0.9)',
      textDecoration: 'none',
      transition: '0.2s all ease-out',
    },
  },
  overline: {
    ...theme.overrides.MuiTypography.overline,
    fontSize: theme.fontSizes.body2,
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
    },
  },
  avatar: {
    height: STUDY_CONTENT_ICON,
    width: STUDY_CONTENT_ICON,
    marginRight: theme.spacing(0.5),
    alignSelf: 'center',
    borderRadius: '50%',
    borderWidth: 2,
    borderStyle: 'solid',
    borderColor: theme.palette.secondary.light,
    '&:hover': {
      borderColor: theme.palette.secondary.main,
    },
  },
  overlinePremiumIcon: {
    fontSize: 20,
    position: 'absolute',
  },
  overlinePremium: {
    color: theme.palette.premium,
    '& span': {
      marginLeft: 24,
      textTransform: 'uppercase',
    },
    '&:hover': {
      cursor: 'help',
    },
  },
  descriptionSmartFolder: {},
  description: {
    lineHeight: 1.75,
    padding: theme.spacing(2, 0, 0, 0),
    color:
      theme.palette.type === 'light'
        ? theme.palette.darkColors.opacity
        : theme.palette.primary.opacity,
    alignSelf: 'flex-start',
    maxWidth: DESCRIPTION_MIN_WIDTH,
    '&$descriptionSmartFolder': {
      paddingBottom: 0,
    },
    [theme.breakpoints.down('sm')]: {
      alignSelf: 'center',
      textAlign: 'center',
      paddingTop: 0,
    },
  },
  title: {
    fontSize: 28,
    fontWeight: theme.fontWeights.semiBold,
    [theme.breakpoints.down('sm')]: {
      fontSize: 20,
      marginTop: 16,
    },
  },
  link: {
    color: theme.palette.secondary.light,
  },
  icon: {
    width: STUDY_CONTENT_ICON,
    height: STUDY_CONTENT_ICON,
    fontSize: STUDY_CONTENT_ICON,
  },
  calendarIcon: {
    width: LEARNING_CONTENT_HERO_ICON,
    height: LEARNING_CONTENT_HERO_ICON,
    fontSize: LEARNING_CONTENT_HERO_ICON,
  },
  folderIcon: {
    width: LEARNING_CONTENT_HERO_ICON,
    height: LEARNING_CONTENT_HERO_ICON,
  },
});

/* -----------------Type declarations--------------- */
type LearningContentHeroClasses = {
  overlinePremium: string,
  overlinePremiumIcon: string,
  avatar: string,
  overline: string,
  overlineOwnerLink: string,
  listAvatar: string,
  listItem: string,
  description: string,
  title: string,
  icon: string,
  descriptionSmartFolder: string,
  folderIcon: string,
  calendarIcon: string,
};

/* -----------------Props--------------- */
type Props = {
  classes: LearningContentHeroClasses,
  metadata: TermMetadata,
  title: string,
  itemCount?: number,
  owner?: Owner,
  premium: boolean,
  description: string,
  type: ContentTypes,
  isSmartFolder?: boolean,
  scheduleSecondaryText?: string,
  isAuthenticated: boolean,
  bundleDetails?: ?BundleDetails,
  folderItemsCount?: FolderItemsCount,
  contentIconUrl?: ?string
};

/* -----------------Component--------------- */
const LearningContentHero = (props: Props) => {
  const {
    classes,
    type,
    metadata,
    title,
    owner,
    itemCount,
    premium,
    bundleDetails,
    description,
    isSmartFolder,
    scheduleSecondaryText,
    isAuthenticated,
    folderItemsCount,
    contentIconUrl,
  } = props;

  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const isPremiumFolder = premium && type === 'FOLDER';
  const getFolderIcon = () => getSmartFolderIcon(folderType[title], classes.folderIcon);

  const renderProgressRing = () => {
    if (isAuthenticated) {
      return (
        <ListItemAvatar className={classes.listAvatar}>
          <ProgressRing
            distribution={metadata.distribution}
            radius={RADIUS_LARGE}
            innerRadius={INNER_RADIUS_LARGE}
            contentIconUrl={contentIconUrl}
            icon={owner ? getContentIcon(type, classes.icon)
              : <CalendarClock className={classes.folderIcon} />}
            largeIcon
          />
        </ListItemAvatar>
      );
    } return (
      <ListItemAvatar className={classes.listAvatar}>
        <ProgressRingUnAuth
          type={type}
          radius={RADIUS_LARGE}
          borderWidth={8}
          contentIconUrl={contentIconUrl}
        />
      </ListItemAvatar>
    );
  };
  const renderListItemAvatar = () => {
    const rootFolderIconUrl = contentIconUrl;
    if (isSmartFolder) {
      return (
        <ListItemAvatar className={classes.listAvatar}>
          {getFolderIcon()}
        </ListItemAvatar>
      );
    }
    if (isPremiumFolder && rootFolderIconUrl) {
      return (
        <ListItemAvatar
          className={classes.listAvatar}
          style={{ borderRadius: '22%', overflow: 'hidden' }}
        >
          <Image
            width={90}
            height={90}
            loading="eager"
            src={rootFolderIconUrl}
            alt={title}
          />
        </ListItemAvatar>
      );
    }
    return renderProgressRing();
  };

  const getDescription = (
    isSmartFolder ? (
      getSmartFolderDesc(folderType[title])
    ) : description
  );

  const DividerPipe = () => (
    <Box display="inline" textAlign="center" width={16}>
      |
    </Box>
  );

  const secondaryText = scheduleSecondaryText || `${type.replace('_', ' ')} | ${getContentItemText(type, itemCount || 0,
    folderItemsCount)}`;
  const showOwner = owner && !isMobile;

  const sendEvent = () => {
    trackCustomEvent({
      eventName: events.username,
    });
  };
  return (
    <>
      <ListItem classes={{ root: classes.listItem }}>
        {!isEmpty(type) ? renderListItemAvatar() : null}
        <ListItemText
          disableTypography
          secondary={(
            <Box mt={1}>
              <Typography variant="overline" className={classes.overline}>
                {secondaryText}
                {!isMobile ? <DividerPipe /> : null }
                {
                  showOwner && owner ? (
                    <Box
                      display="flex"
                      alignItems="center"
                      onClick={sendEvent}
                    >
                      <Link
                        className={classes.overlineOwnerLink}
                        href="/user/[username]"
                        hrefAs={`/user/${owner.name}`}
                      >
                        <>
                          <img src={owner.iconUrl || DefaultAvatar} className={classes.avatar} alt="" />
                          {bundleDetails ? bundleDetails.bundle.publisher.name : owner.name}
                        </>
                      </Link>
                    </Box>
                  ) : null
                }
                {premium && bundleDetails ? (
                  <>
                    <DividerPipe />
                    <Tooltip title={`This premium content belongs to the ${bundleDetails.bundle.name}`}>
                      <Box className={classes.overlinePremium} display="inline-flex" alignItems="baseline">
                        <StudyPackOutline className={classes.overlinePremiumIcon} />
                        {' '}
                        <span>Premium</span>
                      </Box>
                    </Tooltip>
                  </>
                ) : null}
              </Typography>
            </Box>
            )}
          primary={(
            <>
              <Typography variant="h2" className={classes.title}>
                {title}
              </Typography>
            </>
          )}
        />
        <ListItemText />
      </ListItem>
      {description || isSmartFolder ? (
        <Box minHeight="16px">

          <Typography
            variant="body2"
            className={clsx(
              classes.description,
              isSmartFolder && classes.descriptionSmartFolder,
            )}
          >
            <RenderMarkdown
              source={getDescription}
              variant="description"
            />
          </Typography>
        </Box>
      ) : null}
    </>
  );
};

export default withStyles(styles)(LearningContentHero);
